import React, { useState, useEffect, useCallback } from 'react';
import './Admin.css';
import { supabase, isSupabaseConfigured } from './supabaseClient';
import {
  getMessages, updateMessage,
  getCategories, addCategory,
  getWorks, addWork, updateWork, deleteWork,
  getHighlights, setHighlights,
  getHiddenWorks, setHiddenWorks,
  getDeletedWorks, setDeletedWorks,
} from './store';
import { WORKS, mediaUrl, mediaRole, coverUrl, isVideo } from './worksData';

const MEDIA_ROLES = [
  { value: 'principale', label: 'Principale (grande)' },
  { value: 'secondaire', label: 'Secondaire (2 par ligne)' },
  { value: 'sous', label: 'Sous-secondaire (petite)' },
];

// --- Mode local uniquement (tant que Supabase n'est pas configuré) ---
const LOCAL_PASSWORD = 'oblik';
const MAX_ATTEMPTS = 5;
const LOCK_MINUTES = 5;

export default function Admin() {
  // 'loading' | 'signin' | 'mfa-enroll' | 'mfa-verify' | 'ready'
  const [authStep, setAuthStep] = useState('loading');

  // Connexion
  const [email, setEmail] = useState('');
  const [passwordInput, setPasswordInput] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaFactorId, setMfaFactorId] = useState(null);
  const [mfaChallengeId, setMfaChallengeId] = useState(null);
  const [enrollQr, setEnrollQr] = useState(null);
  const [enrollSecret, setEnrollSecret] = useState(null);
  const [authError, setAuthError] = useState('');

  // Données
  const [tab, setTab] = useState('messages');
  const [messages, setMessages] = useState([]);
  const [replyingId, setReplyingId] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [works, setWorks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState('');
  const [form, setForm] = useState({
    title: '', subtitle: '', challenge: '', solution: '', description: '', category: '',
  });
  const [images, setImages] = useState([]); // [{ file, preview, role, cover }] — dans l'ordre d'affichage
  // Visuels mis en avant (démarrage du site + fond de la page d'accueil)
  const [highlights, setHighlightsState] = useState([]);
  const [highlightsSaved, setHighlightsSaved] = useState(false);
  const [highlightsError, setHighlightsError] = useState('');
  // Travaux d'exemple retirés de l'affichage, et ceux écartés définitivement
  const [hiddenWorks, setHiddenWorksState] = useState([]);
  const [deletedWorks, setDeletedWorksState] = useState([]);
  const [publishing, setPublishing] = useState(false);
  const [workError, setWorkError] = useState('');
  // Travail en cours de modification (null = on en crée un nouveau)
  const [editingId, setEditingId] = useState(null);

  /* ================= Authentification ================= */

  const checkAAL = useCallback(async () => {
    const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    if (error) {
      setAuthError(error.message);
      setAuthStep('signin');
      return;
    }
    if (data.currentLevel === 'aal2') {
      setAuthStep('ready');
    } else if (data.nextLevel === 'aal2') {
      // Un facteur TOTP existe : demander le code
      const { data: factors } = await supabase.auth.mfa.listFactors();
      const totp = factors?.totp?.[0];
      if (totp) {
        const { data: challenge, error: chErr } =
          await supabase.auth.mfa.challenge({ factorId: totp.id });
        if (chErr) {
          setAuthError(chErr.message);
          setAuthStep('signin');
          return;
        }
        setMfaFactorId(totp.id);
        setMfaChallengeId(challenge.id);
        setAuthStep('mfa-verify');
      } else {
        setAuthStep('signin');
      }
    } else {
      // Connecté mais aucun facteur : proposer l'activation du 2FA
      startEnroll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isSupabaseConfigured) {
      setAuthStep(
        sessionStorage.getItem('oblik_admin_ok') === '1' ? 'ready' : 'signin'
      );
      return;
    }
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) {
        checkAAL();
      } else {
        setAuthStep('signin');
      }
    });
  }, [checkAAL]);

  const signIn = async (e) => {
    e.preventDefault();
    setAuthError('');

    if (!isSupabaseConfigured) {
      // --- Mode local : mot de passe simple + verrouillage anti-essais ---
      const lockUntil = Number(localStorage.getItem('oblik_admin_lock') || 0);
      if (Date.now() < lockUntil) {
        const minutes = Math.ceil((lockUntil - Date.now()) / 60000);
        setAuthError(`Trop de tentatives. Réessaie dans ${minutes} min.`);
        return;
      }
      if (passwordInput === LOCAL_PASSWORD) {
        localStorage.removeItem('oblik_admin_attempts');
        localStorage.removeItem('oblik_admin_lock');
        sessionStorage.setItem('oblik_admin_ok', '1');
        setAuthStep('ready');
      } else {
        const attempts = Number(localStorage.getItem('oblik_admin_attempts') || 0) + 1;
        if (attempts >= MAX_ATTEMPTS) {
          localStorage.setItem('oblik_admin_lock', String(Date.now() + LOCK_MINUTES * 60000));
          localStorage.setItem('oblik_admin_attempts', '0');
          setAuthError(`Trop de tentatives. Accès bloqué ${LOCK_MINUTES} minutes.`);
        } else {
          localStorage.setItem('oblik_admin_attempts', String(attempts));
          setAuthError(`Mot de passe incorrect (${attempts}/${MAX_ATTEMPTS})`);
        }
        setPasswordInput('');
      }
      return;
    }

    // --- Mode Supabase : vraie connexion ---
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password: passwordInput,
    });
    if (error) {
      setAuthError(error.message === 'Invalid login credentials'
        ? 'Identifiants incorrects'
        : error.message);
      return;
    }
    setPasswordInput('');
    checkAAL();
  };

  const startEnroll = async () => {
    const { data, error } = await supabase.auth.mfa.enroll({ factorType: 'totp' });
    if (error) {
      setAuthError(error.message);
      return;
    }
    setMfaFactorId(data.id);
    setEnrollQr(data.totp.qr_code);
    setEnrollSecret(data.totp.secret);
    setAuthStep('mfa-enroll');
  };

  const verifyEnroll = async (e) => {
    e.preventDefault();
    setAuthError('');
    const { data: challenge, error: chErr } =
      await supabase.auth.mfa.challenge({ factorId: mfaFactorId });
    if (chErr) {
      setAuthError(chErr.message);
      return;
    }
    const { error } = await supabase.auth.mfa.verify({
      factorId: mfaFactorId,
      challengeId: challenge.id,
      code: mfaCode.trim(),
    });
    if (error) {
      setAuthError('Code incorrect, réessaie.');
      return;
    }
    setMfaCode('');
    setAuthStep('ready');
  };

  const verifyMfa = async (e) => {
    e.preventDefault();
    setAuthError('');
    const { error } = await supabase.auth.mfa.verify({
      factorId: mfaFactorId,
      challengeId: mfaChallengeId,
      code: mfaCode.trim(),
    });
    if (error) {
      setAuthError('Code incorrect, réessaie.');
      return;
    }
    setMfaCode('');
    setAuthStep('ready');
  };

  const signOut = async () => {
    if (isSupabaseConfigured) {
      await supabase.auth.signOut();
    } else {
      sessionStorage.removeItem('oblik_admin_ok');
    }
    setAuthStep('signin');
  };

  /* ================= Données ================= */

  const reloadAll = useCallback(() => {
    getMessages().then(setMessages);
    getWorks().then(setWorks);
    getCategories().then(setCategories);
    getHighlights().then((list) => setHighlightsState(list || []));
    getHiddenWorks().then((list) => setHiddenWorksState(list || []));
    getDeletedWorks().then((list) => setDeletedWorksState(list || []));
  }, []);

  useEffect(() => {
    if (authStep === 'ready') reloadAll();
  }, [authStep, reloadAll]);

  const refuse = async (id) => {
    setMessages(await updateMessage(id, { status: 'refused' }));
    if (replyingId === id) setReplyingId(null);
  };

  const publish = async (id) => {
    if (!replyText.trim()) return;
    setMessages(await updateMessage(id, { status: 'published', reply: replyText.trim() }));
    setReplyingId(null);
    setReplyText('');
  };

  const unpublish = async (id) => {
    setMessages(await updateMessage(id, { status: 'refused' }));
  };

  const handleAddCategory = async () => {
    if (!newCategory.trim()) return;
    setCategories(await addCategory(newCategory));
    setForm((f) => ({ ...f, category: newCategory.trim() }));
    setNewCategory('');
  };

  const handleImages = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach((file) => {
      const estVideo = (file.type || '').startsWith('video/');
      const ajouter = (preview) =>
        // Le tout premier visuel devient l'image de garde par défaut
        setImages((prev) => [
          ...prev,
          { file, preview, role: 'principale', cover: prev.length === 0, video: estVideo },
        ]);

      if (estVideo) {
        // Aperçu par URL locale : une vidéo convertie en base64 saturerait
        // la mémoire (un fichier de 20 Mo en ferait presque 27).
        ajouter(URL.createObjectURL(file));
      } else {
        const reader = new FileReader();
        reader.onload = () => ajouter(reader.result);
        reader.readAsDataURL(file);
      }
    });
    e.target.value = '';
  };

  const removeImage = (idx) => {
    setImages((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      // si l'image de garde a été retirée, la première reprend le rôle
      if (next.length > 0 && !next.some((m) => m.cover)) {
        next[0] = { ...next[0], cover: true };
      }
      return next;
    });
  };

  const setImageRole = (idx, role) => {
    setImages((prev) => prev.map((img, i) => (i === idx ? { ...img, role } : img)));
  };

  // Image de garde : une seule à la fois — c'est elle qui représente
  // le travail dans la page Work.
  const setCoverImage = (idx) => {
    setImages((prev) => prev.map((img, i) => ({ ...img, cover: i === idx })));
  };

  const moveImage = (idx, dir) => {
    setImages((prev) => {
      const next = [...prev];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  const submitWork = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.category) {
      alert('Le titre et la catégorie sont obligatoires');
      return;
    }
    if (publishing) return;

    setPublishing(true);
    setWorkError('');
    // On attend la confirmation avant de vider le formulaire : sinon un échec
    // (fichier trop lourd, session expirée…) passerait inaperçu et le travail
    // semblerait publié alors que rien n'a été enregistré.
    const res = editingId
      ? await updateWork(editingId, { ...form, media: images })
      : await addWork({ ...form, media: images });
    setPublishing(false);

    if (!res || res.error) {
      setWorkError((res && res.error) || 'Enregistrement impossible (raison inconnue).');
      return; // le formulaire et les visuels restent en place
    }

    setWorks(await getWorks());
    setForm({ title: '', subtitle: '', challenge: '', solution: '', description: '', category: '' });
    setImages([]);
    setEditingId(null);
    setWorkError('');
  };

  // Charge un travail existant dans le formulaire pour le modifier.
  // Ses visuels sont repris tels quels : on peut en retirer, en ajouter,
  // les réordonner ou changer l'image de garde.
  const editWork = (w) => {
    setEditingId(w.id);
    setWorkError('');
    setForm({
      title: w.title || '',
      subtitle: w.subtitle || '',
      challenge: w.challenge || '',
      solution: w.solution || '',
      description: w.description || '',
      category: w.category || '',
    });
    setImages(
      (w.images || []).map((m) => {
        const url = mediaUrl(m);
        return {
          url,                       // déjà en ligne : pas de fichier à envoyer
          preview: url,
          role: mediaRole(m),
          cover: !!(m && m.cover),
          video: isVideo(url),
        };
      })
    );
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setWorkError('');
    setForm({ title: '', subtitle: '', challenge: '', solution: '', description: '', category: '' });
    setImages([]);
  };

  const removeWork = async (id) => {
    if (window.confirm('Supprimer ce travail ?')) {
      // si c'est celui qu'on est en train de modifier, on referme le formulaire
      if (editingId === id) cancelEdit();
      setWorks(await deleteWork(id));
    }
  };

  /* ---------- Travaux d'exemple : retirer / remettre ---------- */

  const toggleDemoWork = async (label) => {
    const suivant = hiddenWorks.includes(label)
      ? hiddenWorks.filter((l) => l !== label)
      : [...hiddenWorks, label];
    setHiddenWorksState(suivant);
    const res = await setHiddenWorks(suivant);
    if (!res || !res.ok) {
      // échec : on revient à l'état précédent pour ne pas mentir à l'écran
      setHiddenWorksState(hiddenWorks);
      alert("Impossible d'enregistrer : " + ((res && res.reason) || 'erreur inconnue'));
    }
  };

  // Retrait définitif : le travail quitte aussi la liste de l'admin
  const deleteDemoWork = async (label) => {
    if (!window.confirm(
      `Retirer définitivement « ${label} » ?\n\n` +
      `Il disparaîtra du site et de cette liste.`
    )) return;

    const nouveauxSupprimes = [...deletedWorks, label];
    const nouveauxMasques = hiddenWorks.filter((l) => l !== label);
    setDeletedWorksState(nouveauxSupprimes);
    setHiddenWorksState(nouveauxMasques);

    const r1 = await setDeletedWorks(nouveauxSupprimes);
    const r2 = await setHiddenWorks(nouveauxMasques);
    if (!r1 || !r1.ok || !r2 || !r2.ok) {
      setDeletedWorksState(deletedWorks);
      setHiddenWorksState(hiddenWorks);
      alert("Impossible d'enregistrer : " + ((r1 && r1.reason) || (r2 && r2.reason) || 'erreur inconnue'));
    }
  };

  /* ---------- Visuels mis en avant sur l'accueil ---------- */

  // Tous les visuels disponibles : travaux du site + travaux ajoutés en admin
  const availableVisuals = [
    ...WORKS.map((w) => ({ url: mediaUrl(w.image), label: w.label })),
    ...works.map((w) => ({ url: coverUrl(w), label: w.title })),
  ].filter((v) => v.url);

  const toggleHighlight = (url) => {
    setHighlightsSaved(false);
    setHighlightsState((prev) =>
      prev.includes(url) ? prev.filter((u) => u !== url) : [...prev, url]
    );
  };

  const saveHighlights = async () => {
    setHighlightsError('');
    const res = await setHighlights(highlights);
    if (!res || !res.ok) {
      setHighlightsSaved(false);
      setHighlightsError(
        res && res.reason === 'table-manquante'
          ? "La table « settings » n'existe pas encore dans Supabase. Ouvre le SQL Editor de Supabase et exécute le contenu du fichier supabase-setup.sql (partie « Réglages du site »), puis réessaie."
          : `Enregistrement impossible : ${(res && res.reason) || 'erreur inconnue'}`
      );
      return;
    }
    setHighlightsSaved(true);
  };

  /* ================= Rendu ================= */

  if (authStep === 'loading') {
    return <div className="admin-page admin-login"><p>Chargement…</p></div>;
  }

  if (authStep === 'signin') {
    return (
      <div className="admin-page admin-login">
        <form onSubmit={signIn} className="admin-login-box">
          <h1 className="admin-title">Admin</h1>
          {!isSupabaseConfigured && (
            <p className="admin-notice">
              Mode local — les données restent dans ce navigateur.
            </p>
          )}
          {isSupabaseConfigured && (
            <input
              type="email"
              className="admin-input"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
            />
          )}
          <input
            type="password"
            className="admin-input"
            placeholder="Mot de passe"
            value={passwordInput}
            onChange={(e) => setPasswordInput(e.target.value)}
            autoFocus={!isSupabaseConfigured}
          />
          {authError && <p className="admin-error">{authError}</p>}
          <button type="submit" className="admin-btn admin-btn--primary">Entrer</button>
        </form>
      </div>
    );
  }

  if (authStep === 'mfa-enroll') {
    return (
      <div className="admin-page admin-login">
        <form onSubmit={verifyEnroll} className="admin-login-box admin-login-box--wide">
          <h1 className="admin-title">Activer le 2FA</h1>
          <p className="admin-notice">
            Scanne ce QR code avec Google Authenticator (ou équivalent),
            puis entre le code à 6 chiffres pour verrouiller ton compte.
          </p>
          {enrollQr && (
            <img src={enrollQr} alt="QR code 2FA" className="admin-qr" />
          )}
          {enrollSecret && (
            <p className="admin-secret">Clé manuelle : {enrollSecret}</p>
          )}
          <input
            className="admin-input"
            placeholder="Code à 6 chiffres"
            value={mfaCode}
            onChange={(e) => setMfaCode(e.target.value)}
            inputMode="numeric"
            autoFocus
          />
          {authError && <p className="admin-error">{authError}</p>}
          <button type="submit" className="admin-btn admin-btn--primary">Valider</button>
        </form>
      </div>
    );
  }

  if (authStep === 'mfa-verify') {
    return (
      <div className="admin-page admin-login">
        <form onSubmit={verifyMfa} className="admin-login-box">
          <h1 className="admin-title">Code 2FA</h1>
          <p className="admin-notice">Entre le code de ton application d'authentification.</p>
          <input
            className="admin-input"
            placeholder="Code à 6 chiffres"
            value={mfaCode}
            onChange={(e) => setMfaCode(e.target.value)}
            inputMode="numeric"
            autoFocus
          />
          {authError && <p className="admin-error">{authError}</p>}
          <button type="submit" className="admin-btn admin-btn--primary">Vérifier</button>
        </form>
      </div>
    );
  }

  const pending = messages.filter((m) => m.status === 'pending');
  const published = messages.filter((m) => m.status === 'published');

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1 className="admin-title">Admin</h1>
        <button className="admin-btn" onClick={signOut}>Se déconnecter</button>
      </div>

      {!isSupabaseConfigured && (
        <p className="admin-notice">
          Mode local — les données restent dans ce navigateur. Configure
          Supabase (.env.local) pour une vraie base partagée et sécurisée.
        </p>
      )}

      <div className="admin-tabs">
        <button
          className={`admin-tab${tab === 'messages' ? ' admin-tab--active' : ''}`}
          onClick={() => setTab('messages')}
        >
          Messagerie {pending.length > 0 && <span className="admin-badge">{pending.length}</span>}
        </button>
        <button
          className={`admin-tab${tab === 'works' ? ' admin-tab--active' : ''}`}
          onClick={() => setTab('works')}
        >
          Travaux
        </button>
        <button
          className={`admin-tab${tab === 'accueil' ? ' admin-tab--active' : ''}`}
          onClick={() => setTab('accueil')}
        >
          Accueil {highlights.length > 0 && <span className="admin-badge">{highlights.length}</span>}
        </button>
      </div>

      {tab === 'messages' && (
        <div className="admin-section">
          <h2 className="admin-subtitle">En attente ({pending.length})</h2>
          {pending.length === 0 && <p className="admin-empty">Aucun message en attente.</p>}
          {pending.map((m) => (
            <div key={m.id} className="admin-card">
              <div className="admin-card-head">
                <strong>{m.autor}</strong>
                <span className="admin-card-mail">{m.mail}</span>
                <span className="admin-card-date">
                  {new Date(m.date).toLocaleDateString('fr-FR')}
                </span>
              </div>
              <p className="admin-card-content">{m.content}</p>

              {replyingId === m.id ? (
                <div className="admin-reply-zone">
                  <textarea
                    className="admin-textarea"
                    placeholder="Ta réponse..."
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    autoFocus
                  />
                  <div className="admin-actions">
                    <button className="admin-btn admin-btn--primary" onClick={() => publish(m.id)}>
                      Publier la réponse
                    </button>
                    <button className="admin-btn" onClick={() => setReplyingId(null)}>
                      Annuler
                    </button>
                  </div>
                </div>
              ) : (
                <div className="admin-actions">
                  <button
                    className="admin-btn admin-btn--primary"
                    onClick={() => { setReplyingId(m.id); setReplyText(''); }}
                  >
                    Valider &amp; répondre
                  </button>
                  <button className="admin-btn admin-btn--danger" onClick={() => refuse(m.id)}>
                    Refuser
                  </button>
                </div>
              )}
            </div>
          ))}

          <h2 className="admin-subtitle">Publiés ({published.length})</h2>
          {published.length === 0 && <p className="admin-empty">Aucun message publié.</p>}
          {published.map((m) => (
            <div key={m.id} className="admin-card admin-card--published">
              <div className="admin-card-head">
                <strong>{m.autor}</strong>
                <span className="admin-card-mail">{m.mail}</span>
              </div>
              <p className="admin-card-content">{m.content}</p>
              <p className="admin-card-reply">↳ {m.reply}</p>
              <div className="admin-actions">
                <button className="admin-btn admin-btn--danger" onClick={() => unpublish(m.id)}>
                  Retirer du site
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'works' && (
        <div className="admin-section">
          <h2 className="admin-subtitle">
            {editingId ? 'Modifier le travail' : 'Nouveau travail'}
          </h2>
          {editingId && (
            <p className="admin-hint">
              Tu modifies « <strong>{form.title || 'sans titre'}</strong> ». Tu peux
              retirer des visuels, en ajouter, les réordonner ou changer l'image de
              garde, puis enregistrer.
            </p>
          )}
          <form className="admin-form" onSubmit={submitWork}>
            <input
              className="admin-input"
              placeholder="Titre *"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
            <input
              className="admin-input"
              placeholder="Sous-titre"
              value={form.subtitle}
              onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
            />
            <textarea
              className="admin-textarea"
              placeholder="Le challenge — la problématique du client"
              value={form.challenge}
              onChange={(e) => setForm({ ...form, challenge: e.target.value })}
            />
            <textarea
              className="admin-textarea"
              placeholder="La solution — la réponse du studio"
              value={form.solution}
              onChange={(e) => setForm({ ...form, solution: e.target.value })}
            />
            <textarea
              className="admin-textarea"
              placeholder="Le projet — description générale (fin de page)"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />

            <div className="admin-category-row">
              <select
                className="admin-input admin-select"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
              >
                <option value="">Catégorie *</option>
                {categories.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <input
                className="admin-input"
                placeholder="Nouvelle catégorie"
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
              />
              <button type="button" className="admin-btn" onClick={handleAddCategory}>
                + Ajouter
              </button>
            </div>

            <label className="admin-file-label">
              Ajouter des images ou des vidéos
              <input
                type="file"
                accept="image/*,video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov"
                multiple
                onChange={handleImages}
                hidden
              />
            </label>

            {images.length > 0 && (
              <>
                <p className="admin-hint">
                  L'ordre ci-dessous = l'ordre sur la page. Choisis la taille de chacun :
                  une <strong>secondaire</strong> suivie de 2 <strong>sous-secondaires</strong> forme
                  une composition (grande à gauche, 2 petites empilées à droite).
                  L'<strong>image de garde</strong> (★) est celle qui représente le
                  travail dans la page Work — ce peut être une vidéo.
                </p>
                <div className="admin-media-list">
                  {images.map((img, idx) => (
                    <div key={idx} className={`admin-media-item${img.cover ? ' admin-media-item--cover' : ''}`}>
                      <span className="admin-media-order">{idx + 1}</span>
                      {img.video ? (
                        <video
                          src={img.preview}
                          className="admin-media-thumb"
                          muted
                          loop
                          playsInline
                          autoPlay
                        />
                      ) : (
                        <img src={img.preview} alt={`Aperçu ${idx + 1}`} className="admin-media-thumb" />
                      )}
                      <select
                        className="admin-input admin-media-role"
                        value={img.role}
                        onChange={(e) => setImageRole(idx, e.target.value)}
                      >
                        {MEDIA_ROLES.map((r) => (
                          <option key={r.value} value={r.value}>{r.label}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className={`admin-cover-btn${img.cover ? ' admin-cover-btn--on' : ''}`}
                        onClick={() => setCoverImage(idx)}
                        title="Définir comme image de garde"
                      >
                        {img.cover ? '★ Image de garde' : '☆ Image de garde'}
                      </button>
                      <div className="admin-media-actions">
                        <button type="button" onClick={() => moveImage(idx, -1)} disabled={idx === 0} title="Monter">↑</button>
                        <button type="button" onClick={() => moveImage(idx, 1)} disabled={idx === images.length - 1} title="Descendre">↓</button>
                        <button type="button" onClick={() => removeImage(idx)} title="Retirer" className="admin-media-remove">×</button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {workError && (
              <p className="admin-error" role="alert">{workError}</p>
            )}
            <div className="admin-form-actions">
              <button
                type="submit"
                className="admin-btn admin-btn--primary admin-submit"
                disabled={publishing}
              >
                {publishing
                  ? 'Enregistrement en cours…'
                  : editingId ? 'Enregistrer les modifications' : 'Ajouter le travail'}
              </button>
              {editingId && (
                <button type="button" className="admin-btn" onClick={cancelEdit} disabled={publishing}>
                  Annuler
                </button>
              )}
            </div>
          </form>

          <h2 className="admin-subtitle">Travaux existants ({works.length})</h2>
          {works.length === 0 && <p className="admin-empty">Aucun travail ajouté depuis l'admin.</p>}
          <div className="admin-works-list">
            {works.map((w) => {
              const enCours = editingId === w.id;
              const couverture = coverUrl(w);
              const nb = (w.images || []).length;
              return (
                <div key={w.id} className={`admin-work-row${enCours ? ' admin-work-row--editing' : ''}`}>
                  {couverture && (
                    isVideo(couverture)
                      ? <video src={couverture} className="admin-work-thumb" muted playsInline preload="metadata" />
                      : <img src={couverture} alt={w.title} className="admin-work-thumb" />
                  )}
                  <div className="admin-work-info">
                    <strong>{w.title}</strong>
                    <span>
                      {w.category}{w.subtitle ? ` — ${w.subtitle}` : ''}
                      {nb > 0 ? ` · ${nb} visuel${nb > 1 ? 's' : ''}` : ''}
                    </span>
                  </div>
                  <div className="admin-work-actions">
                    <button className="admin-btn" onClick={() => editWork(w)} disabled={enCours}>
                      {enCours ? 'En cours…' : 'Modifier'}
                    </button>
                    <button className="admin-btn admin-btn--danger" onClick={() => removeWork(w.id)}>
                      Supprimer
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {(() => {
            const restants = WORKS.filter((w) => !deletedWorks.includes(w.label));
            const affiches = restants.filter((w) => !hiddenWorks.includes(w.label)).length;
            return (
              <>
                <h2 className="admin-subtitle">
                  Travaux d'exemple ({affiches} affiché{affiches > 1 ? 's' : ''} sur {restants.length})
                </h2>
                <p className="admin-hint">
                  Travaux de démonstration livrés avec le site. <strong>Retirer</strong> les
                  enlève du site (réversible) ; une fois retirés, tu peux les
                  <strong> remettre</strong> ou les <strong>retirer définitivement</strong>,
                  ce qui les fait aussi disparaître de cette liste.
                </p>
                <div className="admin-works-list">
                  {restants.map((w) => {
                    const masque = hiddenWorks.includes(w.label);
                    return (
                      <div key={w.label} className={`admin-work-row${masque ? ' admin-work-row--hidden' : ''}`}>
                        {isVideo(w.image) ? (
                          <video src={w.image} className="admin-work-thumb" muted playsInline preload="metadata" />
                        ) : (
                          <img src={w.image} alt={w.label} className="admin-work-thumb" />
                        )}
                        <div className="admin-work-info">
                          <strong>{w.label}</strong>
                          <span>{masque ? 'Retiré du site' : w.subtitle || ''}</span>
                        </div>
                        {masque ? (
                          <div className="admin-work-actions">
                            <button className="admin-btn" onClick={() => toggleDemoWork(w.label)}>
                              Remettre
                            </button>
                            <button className="admin-btn admin-btn--danger" onClick={() => deleteDemoWork(w.label)}>
                              Retirer définitivement
                            </button>
                          </div>
                        ) : (
                          <button className="admin-btn admin-btn--danger" onClick={() => toggleDemoWork(w.label)}>
                            Retirer
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
                {deletedWorks.length > 0 && (
                  <p className="admin-hint">
                    {deletedWorks.length} travail{deletedWorks.length > 1 ? 'x' : ''} d'exemple
                    retiré{deletedWorks.length > 1 ? 's' : ''} définitivement.
                  </p>
                )}
              </>
            );
          })()}
        </div>
      )}

      {tab === 'accueil' && (
        <div className="admin-section">
          <h2 className="admin-subtitle">Visuels mis en avant</h2>
          <p className="admin-hint">
            Ces visuels s'affichent en plein écran <strong>au démarrage du site</strong> et
            en <strong>fond de la page d'accueil</strong> quand plus personne ne touche
            la souris. Si tu n'en sélectionnes aucun, tous les travaux défilent
            (comportement par défaut).
          </p>

          <div className="admin-highlight-bar">
            <span className="admin-highlight-count">
              {highlights.length === 0
                ? 'Aucun visuel sélectionné — tous les travaux défilent'
                : `${highlights.length} visuel${highlights.length > 1 ? 's' : ''} sélectionné${highlights.length > 1 ? 's' : ''}`}
            </span>
            <div className="admin-highlight-actions">
              {highlights.length > 0 && (
                <button
                  type="button"
                  className="admin-btn"
                  onClick={() => { setHighlightsState([]); setHighlightsSaved(false); }}
                >
                  Tout désélectionner
                </button>
              )}
              <button
                type="button"
                className="admin-btn admin-btn--primary"
                onClick={saveHighlights}
              >
                Enregistrer
              </button>
              {highlightsSaved && <span className="admin-saved">Enregistré ✓</span>}
            </div>
          </div>

          {highlightsError && (
            <p className="admin-error" role="alert">{highlightsError}</p>
          )}

          {/* Sélection en cours : on peut retirer un visuel un par un */}
          {highlights.length > 0 && (
            <>
              <h3 className="admin-subtitle admin-subtitle--small">
                Sélection ({highlights.length}) — dans l'ordre d'apparition
              </h3>
              <div className="admin-selected-list">
                {highlights.map((url, i) => (
                  <div key={`${url}-${i}`} className="admin-selected-item">
                    {isVideo(url) ? (
                      <video src={url} className="admin-selected-media" muted playsInline preload="metadata" />
                    ) : (
                      <img src={url} alt="" className="admin-selected-media" />
                    )}
                    <span className="admin-selected-name">
                      {(availableVisuals.find((v) => v.url === url) || {}).label || 'Visuel'}
                    </span>
                    <button
                      type="button"
                      className="admin-selected-remove"
                      onClick={() => toggleHighlight(url)}
                      title="Retirer de la sélection"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}

          <h3 className="admin-subtitle admin-subtitle--small">
            Tous les visuels — clique pour ajouter ou retirer
          </h3>

          <div className="admin-highlight-grid">
            {availableVisuals.map((v, i) => {
              const selected = highlights.includes(v.url);
              return (
                <button
                  type="button"
                  key={`${v.url}-${i}`}
                  className={`admin-highlight-item${selected ? ' admin-highlight-item--on' : ''}`}
                  onClick={() => toggleHighlight(v.url)}
                  title={v.label}
                >
                  {isVideo(v.url) ? (
                    <video src={v.url} className="admin-highlight-media" muted playsInline preload="metadata" />
                  ) : (
                    <img src={v.url} alt={v.label} className="admin-highlight-media" />
                  )}
                  <span className="admin-highlight-label">{v.label}</span>
                  {selected && <span className="admin-highlight-check">✓</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
